import type { AbsenceWindow, CalendarEvent, ConflictResult, ConflictRule, ConflictRuleContext } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { timeToMinutes } from '@zag/calendar/models';

/**
 * Detects conflicts between scheduled persons and absence calendar events.
 *
 * For each calendar event of type 'absence', checks if the person it applies to
 * (the owner of the personal calendar) is scheduled on that date.
 *
 * Absence events are scoped to the individual person (unlike holidays which are
 * location-scoped). Each absence event has exactly one person in `applicablePersonIds`.
 *
 * Time-aware: a positioned absence window only conflicts with an assigned shift
 * unit whose time range half-open overlaps it. Full-day, all-day, or
 * unpositioned windows conflict with any assignment, as do assignments whose
 * shift times are unknown (fail closed).
 *
 * Returns a ConflictResult per person-per-absence overlap.
 */
export class AbsenceConflictRule implements ConflictRule {
  readonly type = 'absence' as const;

  detect(schedule: ScheduleMap, events: CalendarEvent[], context?: ConflictRuleContext): ConflictResult[] {
    const results: ConflictResult[] = [];

    const absenceEvents = events.filter(e => e.type === 'absence');

    for (const event of absenceEvents) {
      const daySchedule = schedule[event.date];
      if (!daySchedule) continue;

      const applicableSet = new Set(event.applicablePersonIds);

      for (const personId of Object.keys(daySchedule)) {
        if (applicableSet.has(personId) && this.overlapsWindow(event.absenceWindow, daySchedule[personId] ?? [], context)) {
          results.push({
            type: 'absence',
            date: event.date,
            personId,
            eventLabel: event.label,
            calendarId: event.calendarId,
          });
        }
      }
    }

    return results;
  }

  private overlapsWindow(window: AbsenceWindow | undefined, unitIds: string[], context?: ConflictRuleContext): boolean {
    if (!window || window.isAllDay || window.startTime == null || window.endTime == null) return true;
    const windowStart = timeToMinutes(window.startTime);
    const windowEnd = timeToMinutes(window.endTime);
    if (windowStart === null || windowEnd === null) return true;
    if (unitIds.length === 0) return true;
    for (const unitId of unitIds) {
      const meta = context?.shiftUnits?.get(unitId);
      if (!meta) return true;
      const shiftStart = timeToMinutes(meta.startTime);
      const shiftEnd = timeToMinutes(meta.endTime);
      if (shiftStart === null || shiftEnd === null) return true;
      if (windowStart < shiftEnd && shiftStart < windowEnd) return true;
    }
    return false;
  }
}
